import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import Card from '../shared/Card';
import './FloatingCard.css';

const FloatingCard = ({ game, index, onClick }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const cardVariants = {
    initial: { 
      opacity: 0, 
      y: 50,
      rotateX: 10,
      rotateY: index % 2 === 0 ? -10 : 10
    },
    animate: { 
      opacity: 1, 
      y: 0,
      rotateX: 0,
      rotateY: 0,
      transition: {
        duration: 0.8,
        delay: index * 0.2,
        ease: "easeOut"
      }
    },
    hover: {
      y: -10,
      rotateY: index % 2 === 0 ? -5 : 5,
      scale: 1.05,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const getImageSrc = () => {
    if (game.image?.[0]?.uri) {
      return game.image[0].uri;
    }
    
    // Fallback images
    const fallbackImages = {
      'MotoRace': '/images/MotoRace.png',
      'TheraStack': '/images/TheraStack.png',
      'ColorShot': '/images/ColorShot.png'
    };
    
    return fallbackImages[game.name] || '/images/placeholder.png';
  };

  const getGameTags = () => {
    if (game.legacyData?.tags) {
      return game.legacyData.tags.slice(0, 2);
    }
    return [
      game.legacyData?.category || 'Therapy',
      game.legacyData?.difficulty || 'Adaptive'
    ];
  };

  const handleImageLoad = () => setImageLoaded(true);
  const handleImageError = () => setImageError(true);

  const handleCardClick = (e) => {
    e.preventDefault();
    onClick?.(game);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(game);
    }
  };

  return (
    <motion.div
      className={`floating-card floating-card--${index + 1}`}
      variants={cardVariants}
      initial="initial"
      animate="animate"
      whileHover="hover"
    >
      <Card
        variant="dark"
        hover={true}
        glow={true}
        className="game-floating-card"
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        ariaLabel={`Explore ${game.name} - ${game.description?.substring(0, 50)}...`}
      >
        <div className="floating-card-image">
          {!imageLoaded && !imageError && (
            <div className="image-loading" aria-hidden="true">
              <div className="loading-spinner"></div>
            </div>
          )}
          <img
            src={getImageSrc()}
            alt={`${game.name} gameplay screenshot`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ 
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease'
            }}
          />
          <div className="card-overlay" aria-hidden="true">
            <div className="play-icon">▶</div>
          </div>
        </div>
        
        <div className="floating-card-content">
          <h3 className="game-name">{game.name}</h3>
          <p className="game-description">
            {game.description?.substring(0, 60)}...
          </p>
          
          <div className="game-tags" role="list" aria-label="Game categories">
            {getGameTags().map((tag, tagIndex) => (
              <span 
                key={tagIndex} 
                className="game-tag"
                role="listitem"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

FloatingCard.propTypes = {
  game: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    image: PropTypes.arrayOf(PropTypes.shape({
      uri: PropTypes.string
    })),
    legacyData: PropTypes.shape({
      category: PropTypes.string,
      difficulty: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired,
  index: PropTypes.number.isRequired,
  onClick: PropTypes.func
};

export default FloatingCard;